import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { requestJSON } from '../core/http';
import { STORAGE_KEYS } from '../core/storage-keys';
import { createLogger } from '@shared/utils/logger';
import { useSessionStore } from './session-store';

const log = createLogger('UserStore');

export interface EnfaUser {
  id: string;
  name: string;
  email: string;
  avatar?: string | null;
  phone?: string | null;
}

interface RegisterData {
  name: string;
  email: string;
  password: string;
  phone?: string;
}

interface AuthResponse {
  customer: EnfaUser;
  csrf_token?: string;
  customer_ref?: string;
}

type SessionContext = {
  customer_ref?: string;
  customer_id?: string;
  updated_at: string;
};

function persistSessionContext(context: SessionContext | null): void {
  try {
    if (!context) {
      window.sessionStorage.removeItem(STORAGE_KEYS.sessionContext);
      return;
    }
    window.sessionStorage.setItem(STORAGE_KEYS.sessionContext, JSON.stringify(context));
  } catch {
    // Ignore storage restrictions in private mode.
  }
}

export const useUserStore = defineStore('client-user', () => {
  const user = ref<EnfaUser | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const sessionStore = useSessionStore();

  if (window.APP_SESSION?.customer) {
    try {
      const customerData = window.APP_SESSION.customer;
      if (customerData.id !== undefined || customerData.customer_id) {
        user.value = {
          id: customerData.customer_id || String(customerData.id),
          name: customerData.name || '',
          email: customerData.email || '',
          phone: customerData.phone || null,
          avatar: null,
        };
      }
    } catch (err) {
      log.warn('Failed to initialize user from APP_SESSION', err);
    }
  }

  const isAuthenticated = computed(() => Boolean(user.value));

  const fetchUser = async (): Promise<void> => {
    if (isAuthenticated.value) return;
    try {
      const response = await requestJSON<{ customer?: EnfaUser }>('/api/client-auth/me');
      if (response?.customer) {
        user.value = response.customer;
      }
    } catch {
      // Not authenticated — stay in guest mode
    }
  };

  const storeAuthContext = (response: AuthResponse): void => {
    const customerRef = String(response.customer_ref || '').trim();
    const customerId = String(response.customer?.id || '').trim();
    if (!customerRef && !customerId) return;

    persistSessionContext({
      customer_ref: customerRef || undefined,
      customer_id: customerId || undefined,
      updated_at: new Date().toISOString(),
    });
  };

  /**
   * V3.6.7 — Guest→Registered cart persistence.
   *
   * After the customer authenticates, attempt to link the current anonymous
   * dining session to their account.  This is fire-and-forget: failure is
   * non-blocking and the cart (stored in localStorage) is never touched here.
   *
   * Scenarios:
   *   200 OK          → session is now owned by customer (anon_id cleared in DB)
   *   409 CONFLICT    → session already owned by this customer — idempotent, OK
   *   other errors    → logged as warning; UX is unaffected
   */
  const tryLinkGuestSession = async (sessionId: string): Promise<void> => {
    const normalizedId = String(sessionId || '').trim();
    if (!normalizedId) return;
    try {
      await requestJSON('/api/sessions/link-guest', {
        method: 'POST',
        body: { session_id: normalizedId },
      });
      log.info('V3.6.7: guest session linked to registered customer', { session_id: normalizedId });
    } catch (err: unknown) {
      // 409 CONFLICT = already linked to this customer → silent ok
      // Any other error → non-blocking; cart in localStorage is unaffected
      log.warn('V3.6.7: guest session linking skipped (non-blocking)', err);
    }
  };

  const login = async (credentials: { email: string; password: string }) => {
    loading.value = true;
    error.value = null;
    try {
      const response = await requestJSON<AuthResponse>('/api/client-auth/login', {
        method: 'POST',
        body: credentials,
      });

      if (!response?.customer) {
        return false;
      }

      if (typeof window !== 'undefined' && response.csrf_token) {
        (window as any).__ENFA_CSRF_TOKEN__ = response.csrf_token;
      }

      user.value = response.customer;
      storeAuthContext(response);
      // V3.6.7: link active guest session to this customer (fire-and-forget)
      const activeSessionId = String(sessionStore.sessionId || '').trim();
      if (activeSessionId) void tryLinkGuestSession(activeSessionId);
      return true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error al iniciar sesión';
      return false;
    } finally {
      loading.value = false;
    }
  };

  const register = async (userData: RegisterData) => {
    loading.value = true;
    error.value = null;
    try {
      const response = await requestJSON<AuthResponse>('/api/client-auth/register', {
        method: 'POST',
        body: userData,
      });

      if (!response?.customer) {
        return false;
      }

      if (typeof window !== 'undefined' && response.csrf_token) {
        (window as any).__ENFA_CSRF_TOKEN__ = response.csrf_token;
      }

      user.value = response.customer;
      storeAuthContext(response);
      // V3.6.7: link active guest session to this newly registered customer (fire-and-forget)
      const activeSessionId = String(sessionStore.sessionId || '').trim();
      if (activeSessionId) void tryLinkGuestSession(activeSessionId);
      return true;
    } catch (err) {
      error.value = err instanceof Error ? err.message : 'Error en el registro';
      return false;
    } finally {
      loading.value = false;
    }
  };

  const logout = async () => {
    try {
      await requestJSON('/api/client-auth/logout', { method: 'POST' });
    } catch (err) {
      log.warn('Logout API call failed', err);
    } finally {
      user.value = null;
      persistSessionContext(null);
    }
  };

  return {
    user,
    loading,
    error,
    isAuthenticated,
    fetchUser,
    login,
    register,
    logout,
    tryLinkGuestSession,
  };
});
